import { useMutation } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

export type UpdateAccountingReferenceResponse = {
  code: string;
  ids: UUID[];
  message: string;
};

type UpdateAccountingReferenceData = {
  ids: UUID[];
};

type UpdateAccountingReferenceArguments = {
  siteData: UpdateAccountingReferenceData;
  siteId: UUID;
};

/**
 * Updates accounting references for a given site.
 *
 * @param {UUID} siteId - The unique identifier of the site whose accounting references are to be updated.
 * @param {UpdateAccountingReferenceData} siteData - The data containing the accounting references to be updated.
 * @returns {Promise<UpdateAccountingReferenceResponse>} A promise resolving to the server's response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Site/operation/bulk_add_cost_center_to_site_site__site_uuid__accounting_reference_post
 */
export const updateAccountingReferencesOfSite = async (
  siteId: UUID,
  siteData: UpdateAccountingReferenceData,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.SITE.UPDATE_ACCOUNTING_REFERENCES(siteId), {
      json: siteData,
    })
    .json<UpdateAccountingReferenceResponse>();

  return response;
};

/**
 * React Query custom hook for updating accounting references of a site.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationUpdateAccountingReferencesOfSite = (
  options?: Parameters<typeof useMutation>[0],
) => {
  return useMutation({
    mutationFn: async ({
      siteId,
      siteData,
    }: UpdateAccountingReferenceArguments) =>
      updateAccountingReferencesOfSite(siteId, siteData),
    ...options,
  });
};
